import { mapActions, mapGetters } from "vuex";
import toast from "@/utils/toast";
import { cleanObject, formatDate } from "./utils/utils";

export default {
  name: "IdpEnrollmentMain",

  components: {},
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 12,
        order: "desc",
        field: "updated_at",
        search: "",
        learning_type: "structured_learning",
        type: "enrollment",
        idp_period_id: null,
        all: false,
        status: null,
        approval_step: null,
        coaching_type_id: null,
      },
      isLoading: false,
      isModalLoading: false,
      isLoadingSummary: false,
      _searchTimer: null,
      showModal: false,
      modalData: null,
      idpPeriodOptions: [],
      activeFiltersCount: 0,
      termsModalData: {
        trainingData: null,
        enrollmentPayload: null,
      },
      // Configuration flag to switch between PDF and Tabs modal
      // Set to true for tabs-based modal, false for PDF-based modal
      useTabsTermsModal: true, // Change this to switch between modal types
      activeTab: "structured_learning",
      selectedItems: [],
      showConfirmationSubmitModal: false,
      isSubmitting: false,
      projectActivityData: null,
      hasOpenedDeepLink: false,
    };
  },

  computed: {
    ...mapGetters("idpEnrollment", [
      "structuredLearningDetail",
      "socialLearningDetail",
      "experientialLearningDetail",
      "idpLearnings",
      "summaryIdpLearning",
      "trainingMethods",
      "employeeCompetencies",
      "trainingTypes",
      "competencies",
      "listCoaching",
      "employees",
    ]),
    showingFrom() {
      if (!this.idpLearnings.total) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.idpLearnings.total ? this.idpLearnings.total : to;
    },
    approvalStepLabel() {
      const labels = {
        manager: "Manager",
        hse: "HSE Training",
        hr: "HR Operation Site",
      };
      return labels[this.filters.approval_step] || this.filters.approval_step;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    "filters.idp_period_id"() {
      this.filters.page = 1;

      this.loadSummaryLearning(this.filters.learning_type);
      this.loadData();
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("idpEnrollment", [
      "getIdpLearnings",
      "getStructuredLearningDetail",
      "getSocialLearningDetail",
      "getExperientialLearningDetail",
      "submitIdpLearning",
      "getSummaryIdpLearning",
      "getSummaryIdpSocialLearning",
      "getSummaryIdpExperientialLearning",
      "getIdpPeriod",
      "getTrainingMethods",
      "getTrainingTypes",
      "getCompetencies",
      "resetIdpLearnings",
      "getEmployeeCompetencies",
      "getCoachingByCompetency",
      "addSocialLearning",
      "createProjectActivity",
      "updateProjectActivity",
      "deleteProjectActivity",
      "getEmployees",
      "createProjectExperientialLearning",
    ]),

    showFilterModal() {
      this.$bvModal.show("modal-filter");
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        search: this.filters.search,
        all: this.filters.all,
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
        status: this.filters.status,
        approval_step: this.filters.approval_step,
        training_scope: this.filters.training_scope,
        training_category: this.filters.training_category,
        course_type: this.filters.training_category,
        platform: this.filters.platform,
        training_method_id: this.filters.training_method_id,
        training_type: this.filters.training_type,
        competency_id: this.filters.competency_id,
        coaching_type_id: this.filters.coaching_type_id,
      };

      return cleanObject(raw);
    },

    async loadData() {
      try {
        this.isLoading = true;

        const query = this.buildQueryFromFilters();

        await this.getIdpLearnings(query);

        if (this.idpLearnings.page) this.filters.page = this.idpLearnings.page;
        if (this.idpLearnings.limit) this.filters.pageSize = this.idpLearnings.limit;
        this.openDeepLinkTarget();
      } finally {
        this.isLoading = false;
      }
    },

    onTabChange(tab) {
      this.activeTab = tab;

      this.filters.learning_type = tab;
      this.modalData = null;
      this.filters.page = 1;
      this.selectedItems = [];
      this.resetFilters();

      this.loadSummaryLearning(tab);
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", page: 1 };
      this.loadData();
    },

    async showUpdateActivityModal(data) {
      this.projectActivityData = data;

      this.$bvModal.show("project-activity-modal", data);
    },

    async showSubmitProjectConfirmationModal(data) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin mengirim proyek?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitting = true;

            await this.submitIdpLearning({
              query: { learning_type: this.filters.learning_type, type: this.filters.type },
              data: { id: data?.id },
            });

            toast({ type: "success", title: "Success", message: `Proyek berhasil dikirim` });

            this.$bvModal.hide("experiential-learning-modal");
            this.loadData();
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitting = false;
          }
        }
      });
    },

    async addExperientialLearning(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin menambah proyek?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitting = true;

            const formData = new FormData();

            const competency = JSON.stringify(payload.competency);
            const teams = JSON.stringify(payload.teams);

            formData.append("name", payload.name);
            formData.append("description", payload.description);
            formData.append("start_date", payload.start_date);
            formData.append("end_date", payload.end_date);
            formData.append("business_need", payload.business_need);
            formData.append("objectives", payload.objectives);
            formData.append("goals", payload.goals);
            formData.append("scope", payload.scope);
            formData.append("constraints", payload.constraints);
            formData.append("deliverables", payload.deliverables);
            formData.append("impact_a", payload.impact_a);
            formData.append("impact", payload.impact);
            formData.append("impact_b", payload.impact_b);
            formData.append("competency", competency);
            formData.append("teams", teams);
            formData.append("files", payload.files);

            await this.createProjectExperientialLearning({ data: formData });

            toast({ type: "success", title: "Success", message: `Proyek berhasil ditambahkan` });

            this.$bvModal.hide("add-project-modal");
            this.loadData();
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitting = false;
          }
        }
      });
    },

    async showAddActivityModal() {
      this.projectActivityData = null;
      this.$bvModal.show("project-activity-modal");
    },

    async showDeleteActivityConfirmationModal(data) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin menghapus aktivitas proyek?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitting = true;

            await this.deleteProjectActivity({ id: data?.id });

            toast({ type: "success", title: "Success", message: `Aktivitas proyek berhasil dihapus` });

            await this.getExperientialLearningDetail({ id: this.experientialLearningDetail.id });
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitting = false;
          }
        }
      });
    },

    async showConfirmSubmitModal() {
      this.$bvModal.show("confirmation-submit-modal");
    },

    async openDetailModal(row) {
      this.isModalLoading = true;
      this.selectedId = row.id;
      this.showModal = true;

      switch (this.filters.learning_type) {
        case "structured_learning":
          {
            await this.getStructuredLearningDetail({ id: row.id });
          }
          break;
        case "social_learning":
          {
            await this.getSocialLearningDetail({ id: row.id });
          }
          break;
        case "experiential_learning":
          {
            await this.getExperientialLearningDetail({ id: row.id });
          }
          break;
        default:
      }

      this.$nextTick(() => {
        if (this.filters.learning_type === "structured_learning") this.$bvModal.show("structured-learning-modal");

        if (this.filters.learning_type === "social_learning") this.$bvModal.show("social-learning-modal");

        if (this.filters.learning_type === "experiential_learning") this.$bvModal.show("experiential-learning-modal");
      });
      this.isModalLoading = false;
    },

    updateUrlQuery(filters) {
      const query = cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        search: filters.search,
        learning_type: filters.learning_type,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => { });
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 12,
        order: query.order || null,
        field: query.field || null,
        search: query.search || "",
      };

      if (query.learning_type) {
        parsed.learning_type = query.learning_type;
        this.activeTab = query.learning_type;
      }

      Object.keys(parsed).forEach((key) => {
        this.$set(this.filters, key, parsed[key]);
      });
    },

    openDeepLinkTarget() {
      if (this.hasOpenedDeepLink) return;
      if (!this.$route?.query?.course_id && !this.$route?.query?.training_event_id) {
        return;
      }

      const items = this.idpLearnings?.data || [];
      if (!items.length) return;

      const courseId = this.$route?.query?.course_id;
      const search = this.$route?.query?.search;
      let match = null;

      if (courseId) {
        match = items.find((item) =>
          [item.training?.id, item.training_id, item.training?.training_id, item.id]
            .filter(Boolean)
            .some((id) => String(id) === String(courseId))
        );
      }

      if (!match && search) {
        const needle = String(search).toLowerCase();
        const matches = items.filter((item) =>
          String(item.training?.title || item.title || "")
            .toLowerCase()
            .includes(needle)
        );
        if (matches.length === 1) {
          match = matches[0];
        }
      }

      if (!match) return;

      this.hasOpenedDeepLink = true;
      this.openDetailModal(match);
    },

    async submitEnrollment(payload) {
      // Check if this enrollment came from terms modal (service bond)
      const isServiceBond = this.termsModalData.trainingData?.training?.is_service_bond;

      const confirmConfig = {
        title: "Konfirmasi Pendaftaran",
        text: isServiceBond
          ? "Anda telah menyetujui syarat ikatan dinas. Lanjutkan pendaftaran?"
          : "Apakah Anda yakin ingin mengirim pendaftaran?",
        icon: isServiceBond ? "info" : "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      };

      this.$swal(confirmConfig).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.submitIdpLearning({ query: { learning_type: "structured_learning" }, data: { ...payload } });

            const successMessage = isServiceBond
              ? "Pendaftaran berhasil. Perjanjian ikatan dinas kini aktif."
              : "Pendaftaran berhasil!";

            toast({ type: "success", title: "Success", message: successMessage });

            this.$bvModal.hide("structured-learning-modal");
            this.$bvModal.hide("terms-conditions-modal");
            this.$bvModal.hide("terms-conditions-tabs-modal");
            this.resetTermsModal();
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.loadData();
          }
        }
      });
    },

    async submitProjectActivity({ id, data }) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin mengirim aktivitas proyek?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitting = true;

            id ? await this.updateProjectActivity({ id, data }) : await this.createProjectActivity({ data });

            toast({ type: "success", title: "Success", message: id ? "Aktivitas proyek berhasil diperbarui" : "Aktivitas proyek berhasil ditambahkan" });
            await this.getExperientialLearningDetail({ id: this.experientialLearningDetail.id });
            this.$bvModal.hide("project-activity-modal");
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitting = false;
          }
        }
      });
    },

    async addCoaching(data) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin menambah coaching?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitting = true;
            await this.addSocialLearning({ data });

            toast({ type: "success", title: "Success", message: "Coaching berhasil ditambahkan" });
            this.$bvModal.hide("add-coaching-modal");
            this.loadData();
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitting = false;
          }
        }
      });
    },

    async submitCoaching(ids) {
      if (!ids) return;

      try {
        this.isSubmitting = true;
        await this.submitIdpLearning({ query: { learning_type: "social_learning" }, data: { ids: ids } });

        toast({ type: "success", title: "Success", message: "Coaching berhasil dikirim" });
        this.$bvModal.hide("confirmation-submit-modal");
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";
        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.isSubmitting = false;
        this.selectedItems = [];
        this.loadData();
      }
    },

    async loadSummaryLearning(type) {
      this.isLoadingSummary = true;

      const learningType = type || this.filters.learning_type;
      const payload = {
        learning_type: learningType,
      };

      if (this.filters.idp_period_id) payload.idp_period_id = this.filters.idp_period_id;

      switch (learningType) {
        case "structured_learning":
          await this.getSummaryIdpLearning(payload);
          break;
        case "social_learning":
          await this.getSummaryIdpSocialLearning(payload);
          break;
        case "experiential_learning":
          await this.getSummaryIdpExperientialLearning(payload);
          break;
      }

      this.isLoadingSummary = false;
    },

    async loadIdpPeriod() {
      const idpPeriod = await this.getIdpPeriod();
      const now = new Date();

      this.filters.idp_period_id =
        idpPeriod.find((p) => new Date(p.start_date) <= now && new Date(p.end_date) >= now)?.id || null;

      this.idpPeriodOptions = idpPeriod.map((p) => ({
        text: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id,
      }));
    },

    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return "";

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },

    applyFilters(appliedFilters) {
      const filterKeys = [
        "status",
        "approval_step",
        "training_scope",
        "training_category",
        "platform",
        "training_method_id",
        "training_type",
        "competency_id",
        "coaching_type_id",
      ];

      filterKeys.forEach((key) => {
        this.$set(this.filters, key, appliedFilters?.[key] || null);
      });

      this.$set(this.filters, "page", 1);
      this.updateActiveFilterCount();
      this.loadData();

      this.$bvModal.hide("modal-filter");
    },

    updateActiveFilterCount() {
      const { status, approval_step, training_scope, training_category, platform, training_method_id, training_type, competency_id, coaching_type_id } = this.filters;
      const vals = [status, approval_step, training_scope, training_category, platform, training_method_id, training_type, competency_id, coaching_type_id];

      this.activeFiltersCount = vals.filter((x) => x !== null && x !== undefined && x !== "").length;
    },

    resetFilters() {
      const reset = {
        status: null,
        approval_step: null,
        training_scope: null,
        training_category: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        competency_id: null,
        coaching_type_id: null,
        page: 1,
      };

      Object.entries(reset).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.activeFiltersCount = 0;

      this.loadData();
    },

    onSelectChange(course, checked) {
      const existsIdx = this.selectedItems.findIndex((i) => i.id === course.id);

      if (checked) {
        if (existsIdx === -1) this.selectedItems.push(course);
      } else {
        if (existsIdx !== -1) this.selectedItems.splice(existsIdx, 1);
      }
    },

    clearSingleFilter(filterKey) {
      if (this.filters.hasOwnProperty(filterKey)) {
        this.$set(this.filters, filterKey, null);
        this.filters.page = 1;
        this.updateActiveFilterCount();
        this.loadData();
      }
    },

    showTermsModal(data) {
      this.termsModalData = {
        trainingData: data.trainingData,
        enrollmentPayload: data.enrollmentPayload,
      };

      // Hide the current training modal first
      this.$bvModal.hide("structured-learning-modal");

      // Show appropriate terms modal based on configuration
      this.$nextTick(() => {
        const modalId = this.useTabsTermsModal ? "terms-conditions-tabs-modal" : "terms-conditions-modal";
        this.$bvModal.show(modalId);
      });
    },
    proceedWithEnrollment(payload) {
      // User has agreed to terms, proceed with enrollment
      this.submitEnrollment(payload);
    },

    resetTermsModal() {
      this.termsModalData = {
        trainingData: null,
        enrollmentPayload: null,
      };
    },

    onOpenAdd() {
      if (this.activeTab === "social_learning") {
        this.$bvModal.show("add-coaching-modal");
      } else if (this.activeTab === "experiential_learning") {
        this.$bvModal.show("add-project-modal");
      }
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();
    await this.loadIdpPeriod();

    await Promise.all([
      this.loadSummaryLearning(this.filters.learning_type),
      this.getTrainingMethods(),
      this.getTrainingTypes(),
      this.getCompetencies(),
      this.getEmployeeCompetencies(),
      this.loadData(),
      this.getEmployees(),
    ]);
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
    this.resetIdpLearnings();
  },
};
